import OrderSellerRemarksFlag from "@/enum/OrderSellerRemarksFlag";
import mainServerClient from "@/services/http/mainServerClient";

/** 订单匹配方式。 */
export enum BatchUpdateOrderMatchType {
    ORDER_NO = "ORDER_NO",

    WAYBILL_NO = "WAYBILL_NO",
}

/** 批量处理方式。 */
export enum BatchUpdateOrderProcessType {
    MARK = "MARK",

    REMARK = "REMARK",
}

export enum BatchUpdateOrderTaskStatus {
    PENDING = "PENDING",

    RUNNING = "RUNNING",

    COMPLETED = "COMPLETED",

    ERROR = "ERROR",
}

export interface BatchUpdateOrderMarkParams {
    markId?: number;
}

export interface BatchUpdateOrderRemarkParams {
    flag?: OrderSellerRemarksFlag;

    /** 淘宝旗帜标签。 */
    flagTag?: string;

    content?: string;
}

export interface BatchUpdateOrderOperationParams {
    updateMark?: BatchUpdateOrderMarkParams;

    updateRemark?: BatchUpdateOrderRemarkParams;
}

export interface BatchUpdateOrderTask {
    dateCreated?: string;

    lastUpdated?: string;

    id: number;

    companyId?: number;

    clientIp?: string;

    opUserName?: string;

    opUserId?: number;

    opShopId?: number;

    distributedTaskId?: number;

    shopIds?: number[];

    matchType?: BatchUpdateOrderMatchType;

    processType?: BatchUpdateOrderProcessType;

    operationParams?: BatchUpdateOrderOperationParams;

    uploadFileName?: string;

    uploadFileSize?: number;

    uploadFileHash?: string;

    uploadFileUrl?: string;

    inputCount?: number;

    matchedOrderCount?: number;

    successCount?: number;

    failureCount?: number;

    status?: BatchUpdateOrderTaskStatus;

    errorMsg?: string;
}

export interface BatchUpdateOrderTaskListResult {
    items?: BatchUpdateOrderTask[];

    totalCount?: number;

    pageNo?: number;

    pageSize?: number;

    totalPages?: number;
}

export interface CreateBatchUpdateOrderTaskParams {
    shopIds?: number[];

    matchType?: BatchUpdateOrderMatchType;

    processType?: BatchUpdateOrderProcessType;

    markId?: number;

    flag?: OrderSellerRemarksFlag;

    flagTag?: string;

    content?: string;

    file: File;
}

/**
 * 千牛端无法通过 cloud.application.httpRequest 直接提交二进制文件，
 * 因此先上传至淘宝 OSS，再将文件信息提交给主服务。
 */
export interface CreateQnBatchUpdateOrderTaskParams extends Omit<CreateBatchUpdateOrderTaskParams, "file"> {
    excelFileUrl: string;

    excelFileOriginalName: string;

    excelFileSize: number;
}

function appendOptionalValue(formData: FormData, key: string, value: string | number | undefined) {
    if (value === undefined || value === null) return;

    formData.append(key, value.toString());
}

/** 创建订单批量处理任务。 */
export function createBatchUpdateOrderTask(params: CreateBatchUpdateOrderTaskParams) {
    const formData = new FormData();

    params.shopIds?.forEach((shopId) => formData.append("shopIds", shopId.toString()));
    appendOptionalValue(formData, "matchType", params.matchType);
    appendOptionalValue(formData, "processType", params.processType);
    appendOptionalValue(formData, "markId", params.markId);
    appendOptionalValue(formData, "flag", params.flag);
    appendOptionalValue(formData, "flagTag", params.flagTag);
    appendOptionalValue(formData, "content", params.content);
    formData.append("file", params.file);

    return mainServerClient.post<BatchUpdateOrderTask>("/order_batch_process/create", formData, {
        timeout: 60000,
    });
}

/** 千牛端创建接口；文件字段使用千牛上传组件生成的文件信息。 */
export function createQnBatchUpdateOrderTask(params: CreateQnBatchUpdateOrderTaskParams) {
    return mainServerClient.post<BatchUpdateOrderTask>("/order_batch_process/qianniu/create", params, {
        timeout: 60000,
    });
}

/** 获取订单批量处理任务列表。 */
export function getBatchUpdateOrderTasks(params: { pageNo: number; pageSize: number }) {
    return mainServerClient.get<BatchUpdateOrderTaskListResult>("/order_batch_process/list", params);
}

export enum BatchUpdateOrderRecordStatus {
    PENDING = "PENDING",

    SUCCESS = "SUCCESS",

    FAILURE = "FAILURE",

    PARTIAL_SUCCESS = "PARTIAL_SUCCESS",
}

export enum BatchUpdateOrderResultStatus {
    SUCCESS = "SUCCESS",

    FAILURE = "FAILURE",
}

export interface BatchUpdateOrderResult {
    shopId?: number;

    orderId?: number;

    orderOriginalId?: string;

    status?: BatchUpdateOrderResultStatus;

    errorMsg?: string;
}

export interface BatchUpdateOrderRecord {
    id: number;

    taskId?: number;

    sequenceNo?: number;

    inputValue?: string;

    status?: BatchUpdateOrderRecordStatus;

    orderProcessResults?: BatchUpdateOrderResult[];

    errorMsg?: string;

    dateCreated?: string;

    dateUpdated?: string;
}

export interface BatchUpdateOrderRecordListResult {
    items?: BatchUpdateOrderRecord[];

    totalCount?: number;

    pageNo?: number;

    pageSize?: number;

    totalPages?: number;
}

/** 获取订单批量处理输入明细列表。 */
export function getBatchUpdateOrderRecords(params: { taskId: number; pageNo: number; pageSize: number }) {
    return mainServerClient.get<BatchUpdateOrderRecordListResult>("/order_batch_process/record/list", params);
}
